<template>
  <b-modal
    :visible="show"
    :title="isEdit ? 'Chỉnh sửa ngoại lệ bypass' : 'Thêm ngoại lệ bypass'"
    header-bg-variant="light"
    no-close-on-backdrop
    centered
    size="lg"
    body-class="px-4 py-4"
    header-class="px-5 py-4"
    footer-class="px-4 py-3"
    @hidden="resetForm"
    @close="$emit('close')"
    @cancel="$emit('close')"
  >
    <b-form>
      <!-- Loại ngoại lệ -->
      <b-form-group label="Loại ngoại lệ:">
        <b-form-radio-group
          v-model="form.subjectType"
          :options="subjectTypeOptions"
          button-variant="outline-primary"
          size="sm"
          buttons
          @change="onSubjectTypeChange"
        />
      </b-form-group>

      <!-- Đối tượng: nhân viên -->
      <b-form-group
        v-if="form.subjectType === 'employee'"
        label="Nhân viên:"
      >
        <AutoSuggest
          :model.sync="subjectDisplayText"
          :suggestions="employeeSuggestions"
          placeholder="nhân viên"
          suggestion-name="name"
          :loading="isLoadingEmployees"
          :show-clear="true"
          :state="!errors.subjectId"
          error-messages="Vui lòng chọn nhân viên"
          @select="onSubjectSelected"
          @change="onEmployeeSearch"
        />
      </b-form-group>

      <!-- Đối tượng: chức vụ -->
      <b-form-group
        v-if="form.subjectType === 'jobTitle'"
        label="Chức vụ:"
      >
        <AutoSuggest
          :model.sync="subjectDisplayText"
          :suggestions="filteredJobTitleSuggestions"
          placeholder="chức vụ"
          suggestion-name="name"
          :show-clear="true"
          :state="!errors.subjectId"
          error-messages="Vui lòng chọn chức vụ"
          @select="onSubjectSelected"
          @change="onSubjectClearId"
        />
      </b-form-group>

      <!-- Đối tượng: cửa hàng -->
      <b-form-group
        v-if="form.subjectType === 'store'"
        label="Cửa hàng:"
      >
        <AutoSuggest
          :model.sync="subjectDisplayText"
          :suggestions="filteredStoreSuggestions"
          placeholder="cửa hàng"
          suggestion-name="name"
          :show-clear="true"
          :state="!errors.subjectId"
          error-messages="Vui lòng chọn cửa hàng"
          @select="onSubjectSelected"
          @change="onSubjectClearId"
        />
      </b-form-group>

      <!-- Ghi chú -->
      <b-form-group label="Ghi chú:">
        <b-form-textarea
          v-model="form.note"
          size="sm"
          placeholder="Lý do cấp ngoại lệ..."
          rows="2"
        />
      </b-form-group>

      <!-- Thời hạn -->
      <b-form-group label="Thời hạn:">
        <b-form-radio-group v-model="expireMode" class="mb-2">
          <b-form-radio value="unlimited">Vô thời hạn</b-form-radio>
          <b-form-radio value="date">Đến ngày</b-form-radio>
        </b-form-radio-group>
        <date-picker
          v-if="expireMode === 'date'"
          v-model="form.expireAt"
          class="form-control form-control-sm"
          :config="datePickerConfig"
          placeholder="Chọn ngày hết hạn"
        />
        <small v-if="errors.expireAt" class="text-danger">{{ errors.expireAt }}</small>
      </b-form-group>

      <!-- Trạng thái (chỉ hiện khi edit) -->
      <b-form-group v-if="isEdit" label="Trạng thái:">
        <b-form-select
          v-model="form.status"
          :options="statusOptions"
          size="sm"
          class="select-style"
        />
      </b-form-group>
    </b-form>

    <template #modal-footer="{ cancel }">
      <b-button variant="secondary" :disabled="isSubmitting" @click="cancel()">Hủy bỏ</b-button>
      <b-button variant="primary" :disabled="isSubmitting" @click="handleSubmit">
        <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
        {{ isEdit ? 'Cập nhật' : 'Thêm mới' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import datePicker from 'vue-bootstrap-datetimepicker';
import moment from 'moment';
import { IpBypassService } from './service/ip-access-control.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import ApiService from '@/core/services/api.service';
import AutoSuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';

export default {
  name: 'UpsertBypassModal',
  components: { datePicker, AutoSuggest },
  props: {
    show: { type: Boolean, default: false },
    item: { type: Object, default: null },
    isEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      isSubmitting: false,
      isLoadingEmployees: false,
      expireMode: 'unlimited',
      subjectDisplayText: '',
      form: {
        subjectType: 'employee',
        subjectId: null,
        note: '',
        expireAt: '',
        status: 1,
      },
      errors: { subjectId: '', expireAt: '' },
      allJobTitleOptions: [],
      allStoreOptions: [],
      employeeSuggestions: [],
      subjectTypeOptions: [
        { text: 'Nhân viên', value: 'employee' },
        { text: 'Chức vụ', value: 'jobTitle' },
        { text: 'Cửa hàng', value: 'store' },
      ],
      statusOptions: [
        { text: 'Kích hoạt', value: 1 },
        { text: 'Vô hiệu', value: 2 },
      ],
      datePickerConfig: {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        showClear: true,
        showClose: true,
        locale: 'vi',
        minDate: moment().startOf('day'),
      },
    };
  },
  computed: {
    filteredJobTitleSuggestions() {
      const q = (this.subjectDisplayText || '').toLowerCase().trim();
      if (!q) return this.allJobTitleOptions;
      return this.allJobTitleOptions.filter(jt => jt.name.toLowerCase().includes(q));
    },
    filteredStoreSuggestions() {
      const q = (this.subjectDisplayText || '').toLowerCase().trim();
      if (!q) return this.allStoreOptions;
      return this.allStoreOptions.filter(s => s.name.toLowerCase().includes(q));
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.fetchJobTitles();
        this.fetchStores();
        if (this.item) this.initForm(this.item);
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    initForm(item) {
      this.form = {
        subjectType: item.subjectType || 'employee',
        subjectId: item.subjectId || null,
        note: item.note || '',
        expireAt: item.expireAt ? moment(item.expireAt).format('DD/MM/YYYY') : '',
        status: item.status || 1,
      };
      this.expireMode = item.expireAt ? 'date' : 'unlimited';
      this.subjectDisplayText = item.subjectName || '';
      if (item.subjectType === 'employee' && item.subjectId && item.subjectName) {
        this.employeeSuggestions = [{ id: item.subjectId, name: item.subjectName }];
      }
      this.errors = { subjectId: '', expireAt: '' };
    },
    resetForm() {
      this.form = { subjectType: 'employee', subjectId: null, note: '', expireAt: '', status: 1 };
      this.expireMode = 'unlimited';
      this.subjectDisplayText = '';
      this.employeeSuggestions = [];
      this.errors = { subjectId: '', expireAt: '' };
    },
    onSubjectTypeChange() {
      this.form.subjectId = null;
      this.subjectDisplayText = '';
      this.employeeSuggestions = [];
      this.errors.subjectId = '';
    },
    onSubjectSelected(option) {
      if (!option || option.item.id === null) {
        this.form.subjectId = null;
        return;
      }
      this.form.subjectId = option.item.id;
      this.errors.subjectId = '';
    },
    onSubjectClearId() {
      this.form.subjectId = null;
    },
    async onEmployeeSearch(text) {
      this.form.subjectId = null;
      if (!text || text.length < 2) {
        this.employeeSuggestions = [];
        return;
      }
      this.isLoadingEmployees = true;
      try {
        const { data } = await ApiService.query('v1/employees', {
          params: { fullName: text, limit: 20 },
        });
        const list = data.data?.dataset || [];
        this.employeeSuggestions = list.map(e => ({
          id: e.id,
          name: e.storeName ? `${e.fullName} — ${e.storeName}` : e.fullName,
        }));
      } catch {
        this.employeeSuggestions = [];
      } finally {
        this.isLoadingEmployees = false;
      }
    },
    fetchJobTitles() {
      IpBypassService.getJobTitleEnums()
        .then(({ data }) => {
          this.allJobTitleOptions = (data.data || []).map(jt => ({ id: jt.id, name: jt.name }));
        })
        .catch(() => {});
    },
    fetchStores() {
      ApiService.get('v1/stores/all')
        .then(({ data }) => {
          this.allStoreOptions = (data.data || []).map(s => ({ id: s.id, name: s.name }));
        })
        .catch(() => {});
    },
    validateForm() {
      this.errors = { subjectId: '', expireAt: '' };
      let valid = true;
      if (!this.form.subjectId) {
        this.errors.subjectId = 'Vui lòng chọn đối tượng';
        valid = false;
      }
      if (this.expireMode === 'date' && !this.form.expireAt) {
        this.errors.expireAt = 'Vui lòng chọn ngày hết hạn';
        valid = false;
      }
      return valid;
    },
    buildPayload() {
      return {
        subjectType: this.form.subjectType,
        subjectId: this.form.subjectId,
        note: this.form.note || null,
        expireAt: this.expireMode === 'date' && this.form.expireAt
          ? moment(this.form.expireAt, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : null,
        ...(this.isEdit && { status: this.form.status }),
      };
    },
    handleSubmit() {
      if (!this.validateForm()) return;
      this.isSubmitting = true;
      const payload = this.buildPayload();
      const request = this.isEdit
        ? IpBypassService.update(this.item.id, payload)
        : IpBypassService.create(payload);

      request
        .then(() => {
          makeToastSuccess(this.isEdit ? 'Cập nhật thành công!' : 'Thêm mới thành công!');
          this.$emit('saved');
        })
        .catch(err => makeToastFaile(err?.response?.data?.message || 'Lưu thất bại'))
        .finally(() => { this.isSubmitting = false; });
    },
  },
};
</script>
